import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';

import {
  TTenantOwnerSetup,
  tenantOwnerSetupFailure,
} from './entities/tenant-owner-setup.model';

/** Which request of the setup failed, read off its URL. */
function failedRequest(
  error: HttpErrorResponse
):
  | 'app_version'
  | 'platform_subscription_version'
  | 'owner_settings_read'
  | 'unknown' {
  const url = error?.url ?? '';
  if (url.includes('UpdatePlatformSubscriptionVersionAsync')) {
    return 'platform_subscription_version';
  }
  if (url.includes('UpdateAppVersionAsync')) return 'app_version';
  if (url.includes('GetOwnerSettingsByTenantIdAsync')) {
    return 'owner_settings_read';
  }
  return 'unknown';
}

/**
 * Reports an academy left without its app or platform subscription after the
 * automatic retry (spec 036). One issue for the whole case — the fingerprint is
 * fixed — so it can be mapped in Sentry; the tags split it by cause, by which
 * write failed and by the flow that made the academy, and carry the tenant so
 * the academy can be repaired.
 *
 * The HTTP interceptor has already reported each failed request on its own,
 * grouped by endpoint and status; this event is the one that says the outcome.
 */
export function reportTenantOwnerSetupFailure(
  setup: TTenantOwnerSetup,
  error: HttpErrorResponse
): void {
  Sentry.captureMessage(
    'Academia/visitante sem app ou assinatura da plataforma: OwnerSettings não gravado após o retry',
    {
      level: 'error',
      fingerprint: ['tenant-owner-setup-failed'],
      tags: {
        feature: 'tenant_owner_setup',
        tenant_owner_setup_origin: setup.origin,
        tenant_owner_setup_failure: tenantOwnerSetupFailure(error),
        tenant_owner_setup_write: failedRequest(error),
        http_status: String(error?.status ?? ''),
        tenant_id: setup.tenantId,
      },
      extra: {
        appVersion: setup.appVersion,
        platformSubscription: setup.platformSubscription,
        url: error?.url,
        responseBody: error?.error,
      },
    }
  );
}
